import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ThemesCrudService } from '../themes-crud.service.ts';
import {
  findBranchIdInRoute,
  findThemeIdInRoute,
  findWorkspaceIdInRoute,
  WorkspaceGuard,
} from '@tokenbox/services';
import { firstValueFrom } from 'rxjs';

export const ANY_THEME_WILDCARD = 'yyyy';

@Injectable({
  providedIn: 'root',
})
export class ThemeGuard {
  private router = inject(Router);
  private themesCrudService = inject(ThemesCrudService);
  private workspaceGuard = inject(WorkspaceGuard);

  async canActivate(route: ActivatedRouteSnapshot) {
    const workspaceIdByRoute = findWorkspaceIdInRoute(route);
    const branchIdByRoute = findBranchIdInRoute(route);

    if (
      !workspaceIdByRoute ||
      !branchIdByRoute ||
      !(await this.workspaceGuard.canAccessWorkspace(workspaceIdByRoute))
    ) {
      return this.router.parseUrl('/admin/workspaces');
    }

    const allThemes = await firstValueFrom(this.themesCrudService.allThemes$);

    const themeIdByRoute = findThemeIdInRoute(route);
    if (
      typeof themeIdByRoute === 'string' &&
      allThemes.some((theme) => theme.id === themeIdByRoute)
    ) {
      return true;
    }

    const alternativeThemeId = allThemes[0]?.id;
    if (alternativeThemeId) {
      return this.router.parseUrl(
        `/workspaces/${workspaceIdByRoute}/branches/${branchIdByRoute}/design-tokens/themes/${alternativeThemeId}`,
      );
    }

    // A branch always starts with a not-undoable create-default-theme event, so this case should never happen:
    return this.router.parseUrl(
      `/workspaces/${workspaceIdByRoute}/branches/${branchIdByRoute}/design-tokens/themes/create`,
    );
  }
}
